import { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import './ManagedDevices.css';
import { useManagedDeviceTypes } from '../../hooks/useManagedDeviceTypes';
import { useManagedDevices, useMoveManagedDeviceToGroup } from '../../hooks/useManagedDevices';
import {
  useManagedDeviceGroupTree,
  useManagedDeviceGroup,
  useCreateManagedDeviceGroup,
  useUpdateManagedDeviceGroup,
  useDeleteManagedDeviceGroup,
  useMoveManagedDeviceGroup,
} from '../../hooks/useManagedDeviceGroups';
import GroupBreadcrumb from '../../components/Groups/GroupBreadcrumb';
import GroupModal from '../../components/Groups/GroupModal';
import MoveToGroupModal from '../../components/Groups/MoveToGroupModal';
import DeleteGroupModal from '../../components/Groups/DeleteGroupModal';
import DeviceTypeTab from './components/DeviceTypeTab';
import DeviceList from './components/DeviceList';
import DeviceFilters from './components/DeviceFilters';
import CreateDeviceModal from './components/CreateDeviceModal';
import { normalizeTypeSlug, HOSTS_TYPE_SLUG, isMobileType } from './typeSlugs';
import MobileFleet from './mobile/MobileFleet';
import { getChildGroups, findGroupPath, findGroupNode, isDescendantOf, collectSubtreeIds } from './groupTree';
import { Plus, AlertCircle, Loader2, FolderPlus, FolderInput } from 'lucide-react';

/**
 * Página unificada de managed devices: uma aba por tipo e, dentro de cada aba,
 * navegação por grupos (pastas) para a lista não virar um paredão de itens.
 *
 * Os grupos são POR ABA: a árvore é filtrada pelo tipo do device (`typeId`), de
 * forma que uma pasta criada em Switches não aparece em Hosts. Grupos legados sem
 * tipo (a migration não conseguiu classificar) aparecem em todas as abas até
 * serem editados — a edição carimba a aba atual.
 */
const ManagedDevices = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    status: searchParams.get('status') || '',
  });

  const selectedTypeSlug = normalizeTypeSlug(searchParams.get('type')) || HOSTS_TYPE_SLUG;
  const currentGroupId = searchParams.get('group') || null;
  const showAll = searchParams.get('all') === '1';

  const {
    data: deviceTypes,
    isLoading: typesLoading,
    error: typesError,
  } = useManagedDeviceTypes();

  const {
    data: allDevices,
    isLoading: devicesLoading,
    error: devicesError,
    refetch: refetchDevices,
  } = useManagedDevices();

  const selectedType = useMemo(() => {
    if (!deviceTypes) return null;
    return deviceTypes.find((type) => normalizeTypeSlug(type.slug) === selectedTypeSlug) || deviceTypes[0];
  }, [deviceTypes, selectedTypeSlug]);

  const selectedTypeId = selectedType?.id ?? null;
  // Aba Mobile (MDM): lista própria (frota) e cadastro via QR em vez do modal de criação.
  const isMobileTab = isMobileType(selectedType);
  const { data: groupTree = [] } = useManagedDeviceGroupTree(selectedTypeId);

  const createGroup = useCreateManagedDeviceGroup();
  const updateGroup = useUpdateManagedDeviceGroup();
  const deleteGroup = useDeleteManagedDeviceGroup();
  const moveGroup = useMoveManagedDeviceGroup();
  const moveDeviceToGroup = useMoveManagedDeviceToGroup();

  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [moveDevice, setMoveDevice] = useState(null);
  const [deletingGroup, setDeletingGroup] = useState(null);
  const [contextMenu, setContextMenu] = useState(null); // { x, y, device }
  const [dragLabel, setDragLabel] = useState(null); // rótulo do item em arraste

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const updateParams = useCallback((mutate) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      mutate(next);
      return next;
    });
  }, [setSearchParams]);

  // Grupo apagado (ou de outro tenant) na URL: volta para a raiz em vez de
  // deixar a lista vazia sem explicação.
  useEffect(() => {
    if (!currentGroupId || groupTree.length === 0) return;
    if (!findGroupNode(groupTree, currentGroupId)) {
      updateParams((p) => p.delete('group'));
    }
  }, [currentGroupId, groupTree, updateParams]);

  // Fecha o menu de contexto ao clicar em qualquer lugar
  useEffect(() => {
    if (!contextMenu) return undefined;
    const handler = () => setContextMenu(null);
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, [contextMenu]);

  // Devices do tipo da aba já com busca/status aplicados. Serve tanto para a
  // lista do nível atual quanto para as contagens e para o conteúdo de um
  // grupo expandido no "+".
  const typeDevices = useMemo(() => {
    if (!allDevices || !selectedType) return [];

    const typeKindSlugs = new Set((selectedType.kindDefinitions || []).map((kind) => kind.slug));
    let filtered = allDevices.filter((device) => typeKindSlugs.has(device.kind));

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((device) =>
        String(device.name || '').toLowerCase().includes(searchLower) ||
        String(device.description || '').toLowerCase().includes(searchLower) ||
        String(device.vpnIp || '').includes(searchLower) ||
        (device.vpnLinks || []).some((l) => String(l.vpnIp || '').includes(searchLower))
      );
    }

    if (filters.status) {
      filtered = filtered.filter((device) => device.status === filters.status);
    }

    return filtered;
  }, [allDevices, selectedType, filters]);

  // Devices soltos no nível atual (ou todos, com "mostrar tudo" ligado).
  const visibleDevices = useMemo(() => {
    if (showAll) return typeDevices;
    return typeDevices.filter((device) => (device.managedDeviceGroupId ?? null) === currentGroupId);
  }, [typeDevices, showAll, currentGroupId]);

  // O /tree e filtrado por aba e nao traz ManagedDeviceCount. Para o aviso de
  // exclusao precisamos do total REAL do grupo (inclusive itens de outras abas),
  // entao buscamos o grupo cheio so enquanto o modal esta aberto.
  const { data: deletingGroupDetail, isLoading: deletingGroupLoading } = useManagedDeviceGroup(deletingGroup?.id);

  const groupPath = useMemo(() => findGroupPath(groupTree, currentGroupId), [groupTree, currentGroupId]);

  // Aba Mobile: ações/política/versão de grupo valem para o grupo + subgrupos (o servidor resolve);
  // aqui só a contagem, sem busca/status, para bater com o que o servidor vai atingir.
  const mobileGroupIds = useMemo(
    () => (isMobileTab ? new Set(groupTree.flatMap((node) => collectSubtreeIds(node))) : null),
    [isMobileTab, groupTree]
  );
  const scopeDeviceCount = useMemo(() => {
    if (!isMobileTab || !allDevices || !selectedType) return 0;
    const kinds = new Set((selectedType.kindDefinitions || []).map((kind) => kind.slug));
    const ofType = allDevices.filter((device) => kinds.has(device.kind));
    if (!currentGroupId) return ofType.length;
    const ids = new Set(collectSubtreeIds(findGroupNode(groupTree, currentGroupId)));
    return ofType.filter((device) => ids.has(device.managedDeviceGroupId)).length;
  }, [isMobileTab, allDevices, selectedType, currentGroupId, groupTree]);
  const visibleGroups = useMemo(() => getChildGroups(groupTree, currentGroupId), [groupTree, currentGroupId]);
  const parentGroupId = groupPath.length >= 2 ? groupPath[groupPath.length - 2].id : null;

  const handleTypeChange = (typeSlug) => {
    updateParams((next) => {
      next.set('type', typeSlug);
      next.delete('group');
    });
    setExpandedGroups(new Set());
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    updateParams((next) => {
      if (newFilters.search) next.set('search', newFilters.search);
      else next.delete('search');

      if (newFilters.status) next.set('status', newFilters.status);
      else next.delete('status');
    });
  };

  const handleShowAllChange = (value) => {
    updateParams((next) => {
      if (value) next.set('all', '1');
      else next.delete('all');
    });
  };

  const navigateToGroup = (groupId) => {
    updateParams((next) => {
      if (groupId) next.set('group', groupId);
      else next.delete('group');
    });
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const handleSaveGroup = async (formData) => {
    try {
      // typeId prende o grupo à aba atual (e classifica grupo legado sem tipo).
      if (editingGroup) {
        await updateGroup.mutateAsync({ id: editingGroup.id, data: { ...formData, typeId: selectedTypeId } });
      } else {
        await createGroup.mutateAsync({ ...formData, parentGroupId: currentGroupId, typeId: selectedTypeId });
      }
      setGroupModalOpen(false);
      setEditingGroup(null);
    } catch (e) {
      alert(e.response?.data?.message || e.message || t('managedDevices.groups.saveError'));
    }
  };

  const handleDeleteGroup = async () => {
    if (!deletingGroup) return;
    try {
      await deleteGroup.mutateAsync(deletingGroup.id);
      // O serviço sobe os devices do grupo para o pai — a lista precisa recarregar.
      refetchDevices();
      if (currentGroupId === deletingGroup.id) navigateToGroup(parentGroupId);
      setDeletingGroup(null);
    } catch (e) {
      alert(e.response?.data?.message || e.message || t('managedDevices.groups.deleteError'));
    }
  };

  // Para onde os itens vão quando o grupo some: o pai do grupo, ou a lista principal.
  const deleteDestinationName = (() => {
    if (!deletingGroup) return '';
    const parentId = deletingGroup.parentGroupId ?? null;
    if (!parentId) return t('groups.breadcrumbRoot');
    return findGroupNode(groupTree, parentId)?.name || t('groups.breadcrumbRoot');
  })();

  const handleMoveDeviceToGroup = async (groupId) => {
    if (!moveDevice) return;
    try {
      await moveDeviceToGroup.mutateAsync({ id: moveDevice.id, groupId });
      setMoveDevice(null);
    } catch (e) {
      alert(e.response?.data?.message || e.message || t('managedDevices.groups.moveError'));
    }
  };

  const handleDragStart = (event) => {
    const data = event.active.data.current;
    if (data?.type === 'device') {
      setDragLabel(typeDevices.find((d) => d.id === data.deviceId)?.name || null);
    } else if (data?.type === 'folder') {
      setDragLabel(findGroupNode(groupTree, data.groupId)?.name || null);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setDragLabel(null);
    if (!over) return;

    const src = active.data.current;
    const dst = over.data.current;
    if (!src || dst?.type !== 'group') return;

    const targetGroupId = dst.groupId ?? null;

    try {
      if (src.type === 'device') {
        if ((src.groupId ?? null) === targetGroupId) return;
        await moveDeviceToGroup.mutateAsync({ id: src.deviceId, groupId: targetGroupId });
      } else if (src.type === 'folder') {
        // Mover um grupo para dentro dele mesmo (ou de um filho) quebraria a árvore.
        if (isDescendantOf(groupTree, targetGroupId, src.groupId)) return;
        await moveGroup.mutateAsync({ id: src.groupId, parentGroupId: targetGroupId });
      }
    } catch (e) {
      alert(e.response?.data?.message || e.message || t('managedDevices.groups.moveError'));
    }
  };

  if (typesLoading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <Loader2 className="h-8 w-8 animate-spin text-theme-primary" />
        <span className="ml-2 text-gray-600">{t('managedDevices.loadingTypes')}</span>
      </div>
    );
  }

  if (typesError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start">
          <AlertCircle className="h-6 w-6 text-red-600 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-lg font-medium text-red-800">{t('managedDevices.errorLoadingTypes')}</h3>
            <p className="text-red-700 mt-2">{t('managedDevices.errorLoadingTypes')} {typesError.message}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
            >
              {t('common.buttons.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!deviceTypes || deviceTypes.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-start">
          <AlertCircle className="h-6 w-6 text-yellow-600 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-lg font-medium text-yellow-800">{t('managedDevices.noTypes')}</h3>
            <p className="text-yellow-700 mt-2">{t('managedDevices.noTypesHint')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={() => setDragLabel(null)}>
      <div className="p-6 space-y-6">
        {/* Page Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('managedDevices.title')}</h1>
            <p className="text-gray-600 mt-1">{t('managedDevices.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setEditingGroup(null); setGroupModalOpen(true); }}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FolderPlus size={18} />
              {t('managedDevices.groups.newGroup')}
            </button>
            {!isMobileTab && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-theme-primary text-white rounded-lg hover:bg-theme-primary/90 transition-colors"
              >
                <Plus size={18} />
                {t('managedDevices.new')}
              </button>
            )}
          </div>
        </div>

        {/* Device Type Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex flex-wrap gap-2 pb-4">
            {deviceTypes.map((type) => (
              <DeviceTypeTab
                key={type.id}
                type={type}
                isActive={normalizeTypeSlug(type.slug) === selectedTypeSlug}
                onClick={() => handleTypeChange(type.slug)}
                deviceCount={allDevices ? allDevices.filter((device) =>
                  type.kindDefinitions?.some((kind) => kind.slug === device.kind)
                ).length : 0}
              />
            ))}
          </div>
        </div>

        {/* Filters */}
        <DeviceFilters
          filters={filters}
          onChange={handleFiltersChange}
          selectedType={selectedType}
          showAll={showAll}
          onShowAllChange={handleShowAllChange}
        />

        {/* Navegação de grupos dentro da aba */}
        {!showAll && (
          <GroupBreadcrumb path={groupPath} onNavigate={navigateToGroup} />
        )}

        {/* Device List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Falha num refetch com a lista já carregada (ex.: API reiniciando no deploy): mantém a
              lista e só avisa — o polling de 8s se recupera sozinho. */}
          {devicesError && allDevices && (
            <div className="flex items-center gap-2 px-6 py-2 text-xs text-amber-700 bg-amber-50 border-b border-amber-200">
              <Loader2 className="h-3 w-3 animate-spin" />
              {t('managedDevices.reconnecting')}
            </div>
          )}
          {devicesError && !allDevices ? (
            <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                <span className="text-red-700">{t('managedDevices.errorLoading')} {devicesError.message}</span>
              </div>
            </div>
          ) : isMobileTab ? (
            <MobileFleet
              devices={visibleDevices}
              groups={visibleGroups}
              isLoading={devicesLoading}
              currentGroupId={currentGroupId}
              currentGroupName={groupPath.length > 0 ? groupPath[groupPath.length - 1].name : null}
              scopeDeviceCount={scopeDeviceCount}
              mobileGroupIds={mobileGroupIds}
              showAll={showAll}
              onOpenGroup={(group) => navigateToGroup(group.id)}
            />
          ) : (
            <DeviceList
              devices={visibleDevices}
              typeDevices={typeDevices}
              selectedType={selectedType}
              isLoading={devicesLoading}
              onRefresh={refetchDevices}
              groups={visibleGroups}
              showAll={showAll}
              currentGroupId={currentGroupId}
              parentGroupId={parentGroupId}
              expandedGroups={expandedGroups}
              onToggleGroup={toggleGroup}
              onOpenGroup={(group) => navigateToGroup(group.id)}
              onNavigateGroup={navigateToGroup}
              onEditGroup={(group) => { setEditingGroup(group); setGroupModalOpen(true); }}
              onDeleteGroup={(group) => setDeletingGroup(group)}
              onMoveDevice={(device) => setMoveDevice(device)}
              onDeviceContextMenu={(e, device) => setContextMenu({ x: e.clientX, y: e.clientY, device })}
            />
          )}
        </div>

        {/* Create Device Modal */}
        <CreateDeviceModal
          show={showCreateModal}
          onHide={() => setShowCreateModal(false)}
          onDeviceCreated={() => { setShowCreateModal(false); refetchDevices(); }}
          selectedType={selectedType}
        />

        {/* Criar / editar grupo */}
        <GroupModal
          isOpen={groupModalOpen}
          onClose={() => { setGroupModalOpen(false); setEditingGroup(null); }}
          onSave={handleSaveGroup}
          initialData={editingGroup}
          loading={createGroup.isPending || updateGroup.isPending}
          parentGroupName={!editingGroup && groupPath.length > 0 ? groupPath[groupPath.length - 1].name : null}
        />

        {/* Mover device para grupo */}
        {moveDevice && (
          <MoveToGroupModal
            isOpen
            onClose={() => setMoveDevice(null)}
            onMove={handleMoveDeviceToGroup}
            itemName={moveDevice.name}
            currentGroupId={moveDevice.managedDeviceGroupId ?? null}
            tree={groupTree}
            loading={moveDeviceToGroup.isPending}
          />
        )}

        {/* Excluir grupo */}
        <DeleteGroupModal
          isOpen={!!deletingGroup}
          onClose={() => setDeletingGroup(null)}
          onConfirm={handleDeleteGroup}
          group={deletingGroup}
          deviceCount={deletingGroupDetail?.managedDeviceCount ?? 0}
          subGroupCount={deletingGroupDetail?.childGroupCount ?? 0}
          destinationName={deleteDestinationName}
          loading={deleteGroup.isPending}
          countsLoading={!!deletingGroup && (deletingGroupLoading || !deletingGroupDetail)}
        />

        {/* Menu de contexto do device */}
        {contextMenu && (
          <div
            className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-48"
            style={{ top: contextMenu.y, left: contextMenu.x }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => { setMoveDevice(contextMenu.device); setContextMenu(null); }}
            >
              <FolderInput size={14} />
              {t('managedDevices.groups.moveToGroup')}
            </button>
          </div>
        )}
        <DragOverlay dropAnimation={null}>
          {dragLabel && (
            <div className="pointer-events-none rounded-lg border border-theme-primary bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-lg">
              {dragLabel}
            </div>
          )}
        </DragOverlay>
      </div>
    </DndContext>
  );
};

export default ManagedDevices;
